import { Component, computed, inject, signal } from "@angular/core"
import { ActivatedRoute, Router, RouterModule } from "@angular/router"
import { MatIconModule } from "@angular/material/icon"
import { AppBarComponent } from "../../../../shared/components/app-bar/app-bar.component"
import { MainContentComponent } from "../../../../shared/components/main-content/main-content.component"
import { SeoComponent } from "../../../../shared/components/seo/seo.component"
import { ERoutes } from "../../../../shared/constants/routes.enum"
import { toObservable } from "@angular/core/rxjs-interop"
import {
  catchError,
  combineLatest,
  finalize,
  forkJoin,
  of,
  switchMap,
  takeUntil,
} from "rxjs"
import {
  RecommendationService,
  RecommendationTimeSlot,
  RecommendationUseCase,
  INTERNET_ACTIVITIES,
  SpotRecommendationService,
} from "../../../services/spot-recommendation.service"
import {
  IMonitoredSpot,
  ISpotRecommendation,
} from "../../../interfaces/spot-recommendation.interface"

type QualityLabel = "Excellent" | "Good" | "Fair" | "Poor" | "Low data"
type Confidence = "High" | "Medium" | "Low"
type Availability = "Available" | "Moderate" | "Busy" | "Very Busy" | "No data"

interface InternetSpot {
  id: string | number
  rank: number
  name: string
  meta: string
  quality: QualityLabel
  confidence: Confidence
}

interface AvailabilitySpot {
  id: string | number
  name: string
  meta: string
  availability: Availability
  icon: string
}

interface DirectorySpot {
  id: string | number
  name: string
  building: string
  icon: string
  updatedAgo: string
}

@Component({
  selector: "app-spots",
  imports: [
    RouterModule,
    MatIconModule,
    AppBarComponent,
    MainContentComponent,
  ],
  templateUrl: "./spots.component.html",
  styleUrl: "./spots.component.scss",
})
export class SpotsComponent extends SeoComponent {
  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly recommendations = inject(SpotRecommendationService)

  readonly eRoutes = ERoutes

  readonly activeView = signal<"spots" | "analysis">(
    this.route.snapshot.queryParamMap.get("view") === "analysis"
      ? "analysis"
      : "spots",
  )
  readonly activeCategory = signal<RecommendationService>("internet")
  readonly activeActivity = signal<RecommendationUseCase>("web")
  readonly activeTime = signal<RecommendationTimeSlot>("now")
  readonly searchQuery = signal("")
  readonly recommendationsLoading = signal(true)
  readonly recommendationsError = signal(false)
  readonly directoryLoading = signal(true)
  readonly directoryError = signal(false)

  readonly spotCategories: { id: RecommendationService; label: string }[] = [
    { id: "internet", label: "Internet" },
    { id: "study", label: "Study" },
    { id: "cafe", label: "Cafe" },
    { id: "classroom", label: "Classroom" },
    { id: "lab", label: "Lab" },
    { id: "other", label: "Other" },
  ]

  readonly activityFilters: { id: RecommendationUseCase; label: string }[] = [
    ...INTERNET_ACTIVITIES,
  ]

  readonly timeFilters: { id: RecommendationTimeSlot; label: string }[] = [
    { id: "now", label: "Now" },
    { id: "morning", label: "Morning" },
    { id: "lunch", label: "Lunch" },
    { id: "afternoon", label: "Afternoon" },
    { id: "evening", label: "Evening" },
    { id: "night", label: "Night" },
  ]

  readonly filteredInternetSpots = signal<InternetSpot[]>([])
  readonly availabilitySpots = signal<AvailabilitySpot[]>([])
  private readonly directorySpots = signal<DirectorySpot[]>([])
  readonly filteredDirectory = computed(() => {
    const query = this.searchQuery().trim().toLowerCase()
    if (!query) {
      return this.directorySpots()
    }
    return this.directorySpots().filter(
      (spot) =>
        spot.name.toLowerCase().includes(query) ||
        spot.building.toLowerCase().includes(query),
    )
  })
  readonly directoryTotal = signal(0)

  constructor() {
    super()
    this.title = "Spots"
    combineLatest([
      toObservable(this.activeCategory),
      toObservable(this.activeActivity),
      toObservable(this.activeTime),
    ])
      .pipe(
        switchMap(([service, useCase, timeSlot]) => {
          this.recommendationsLoading.set(true)
          this.recommendationsError.set(false)
          return this.recommendations
            .getRecommendations(
              service,
              timeSlot,
              service === "internet" ? useCase : undefined,
            )
            .pipe(
              catchError(() => {
                this.recommendationsError.set(true)
                return of([])
              }),
              finalize(() => this.recommendationsLoading.set(false)),
            )
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((spots) => this.setRecommendations(spots))

    forkJoin({
      page: this.recommendations.getMonitoredSpots(),
      // Omitting the use case aggregates all activities. Check every time slot
      // so a quiet "Now" window does not hide spots with data at other times.
      internetSpotsByTime: forkJoin(
        this.timeFilters.map(({ id }) =>
          this.recommendations.getRecommendations("internet", id),
        ),
      ),
    })
      .pipe(
        catchError(() => {
          this.directoryError.set(true)
          return of(null)
        }),
        finalize(() => this.directoryLoading.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((result) => {
        if (!result) return
        const ratedSpotIds = new Set(
          result.internetSpotsByTime.flat()
            .filter((spot) => spot.sampleCount > 0 && this.qualityLabel(spot.qoeBadge) !== "Low data")
            .map((spot) => spot.spotId),
        )
        const spots = result.page.content.filter((spot) => ratedSpotIds.has(spot.id))
        this.directoryTotal.set(spots.length)
        this.directorySpots.set(spots.map((spot) => this.toCatalogSpot(spot)))
      })
  }

  setView(view: "spots" | "analysis"): void {
    this.activeView.set(view)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view },
      queryParamsHandling: "merge",
      replaceUrl: true,
    })
  }

  setCategory(category: RecommendationService): void {
    this.activeCategory.set(category)
  }

  setActivity(activity: RecommendationUseCase): void {
    this.activeActivity.set(activity)
  }

  setTime(time: RecommendationTimeSlot): void {
    this.activeTime.set(time)
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value
    this.searchQuery.set(value)
  }

  openSpot(id: string | number): void {
    this.router.navigate([this.i18nStore.routeRoot, ERoutes.SPOT_DETAILS], {
      queryParams: {
        spot: id,
        time_slot: this.activeView() === "analysis" ? "now" : this.activeTime(),
      },
    })
  }

  routeLink(route: string): string[] {
    return [this.i18nStore.routeRoot, route]
  }

  qualityClass(quality: QualityLabel): string {
    return quality.toLowerCase().replaceAll(" ", "")
  }

  private setRecommendations(spots: ISpotRecommendation[]): void {
    if (this.activeCategory() === "internet") {
      this.filteredInternetSpots.set(spots.map((spot) => this.toInternetSpot(spot)))
      this.availabilitySpots.set([])
      return
    }
    this.filteredInternetSpots.set([])
    this.availabilitySpots.set(
      spots.map((spot) => ({
        id: spot.spotId,
        name: spot.spotName,
        meta: spot.location ?? spot.buildingName ?? "Campus location",
        availability: spot.sampleCount > 0 ? this.availabilityLabel(spot.occupancyStatus) : "No data",
        icon:
          this.activeCategory() === "cafe"
            ? "local_cafe"
            : this.activeCategory() === "study"
              ? "menu_book"
              : "apartment",
      })),
    )
  }

  private toInternetSpot(spot: ISpotRecommendation): InternetSpot {
    const confidence = spot.confidence ?? 0
    return {
      id: spot.spotId,
      rank: spot.rank,
      name: spot.spotName,
      meta: [
        spot.location,
        spot.walkTimeMinutes != null ? `${spot.walkTimeMinutes} min walk` : null,
      ]
        .filter(Boolean)
        .join(" · "),
      quality: this.qualityLabel(spot.qoeBadge),
      confidence:
        confidence >= 0.75 ? "High" : confidence >= 0.4 ? "Medium" : "Low",
    }
  }

  private toCatalogSpot(spot: IMonitoredSpot): DirectorySpot {
    const location = [
      spot.building?.commonName,
      spot.building?.compound,
      spot.floorNumber != null ? `Floor ${spot.floorNumber}` : null,
      spot.description,
    ]
      .filter(Boolean)
      .join(" · ")
    return {
      id: spot.id,
      name: spot.commonName,
      building: location || spot.officialName || spot.spotType || "Campus location",
      icon: spot.spotType?.toUpperCase() === "INDOOR" ? "apartment" : "place",
      updatedAgo: spot.lastMonitored,
    }
  }

  private qualityLabel(badge: string | null): QualityLabel {
    return badge === "Excellent" || badge === "Good" || badge === "Fair" || badge === "Poor"
      ? badge
      : "Low data"
  }

  private availabilityLabel(status: string | null): Availability {
    switch (status?.toUpperCase()) {
      case "AVAILABLE":
        return "Available"
      case "MODERATE":
        return "Moderate"
      case "VERY_BUSY":
        return "Very Busy"
      default:
        return "No data"
    }
  }
}
